import { useCallback, useEffect, useRef, useState, useActionState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import InvitationCodeCard from "components/card/invitation-code/InvitationCodeCard";
import { useOffer } from "hooks/useOffer";
import { useAutomaticOfferLookup } from "hooks/useAutomaticOfferLookup";
import { useQuote } from "hooks";
import { clearPersistedState } from "store/persistUtils";
import { resetApplication } from "store/application/applicationSlice";
import { useProductContext } from "context/ProductContext";
import { useUserInteraction } from "hooks/useUserInteraction";

const InvitationCodePage = () => {
    // ========== Hooks ==========
    const dispatch = useDispatch();
    const { validateAndProcessOffer, isLoading: offerLoading, error } = useOffer();
    const navigate = useNavigate();
    const { loadSavedQuoteData } = useQuote();
    const { isDTO, isGIWL } = useProductContext();
    const { sendUserInteraction } = useUserInteraction("welcome");

    // ========== State ==========
    const [isResetComplete, setIsResetComplete] = useState(false);
    const hasSentWelcomeInteraction = useRef(false);

    // ========== Effects ==========
    useEffect(() => {
        clearPersistedState();
        dispatch(resetApplication());
        setIsResetComplete(true);
    }, [dispatch]);

    // Send welcome page interaction after reset completes.
    // The reset generates a new stable sessionId. By waiting for the re-render
    // triggered by setIsResetComplete, sendUserInteraction captures the new sessionId.
    useEffect(() => {
        if (isResetComplete && !hasSentWelcomeInteraction.current) {
            hasSentWelcomeInteraction.current = true;
            sendUserInteraction();
        }
    }, [isResetComplete, sendUserInteraction]);

    // ========== Event Handlers ==========
    const handleOfferResult = useCallback(
        async (code: string, isPurl: boolean) => {
            const result = await validateAndProcessOffer(code, isPurl);
            if (result.success && result.route) {
                // Load saved quote data before navigating
                if (result.route === "/quote" && (isDTO || isGIWL)) {
                    const applicantType = result.offer?.memberOfferAvailable ? "member" : "spouse";
                    const coverageAmounts = result.offer?.offer
                        ? result.offer.offer.split("|").map((amount: string) => +amount)
                        : [];
                    await loadSavedQuoteData(result.offer?.finderNumber, applicantType, coverageAmounts);
                }

                navigate(result.route);
                setTimeout(() => {
                    document.getElementById("hero-card")?.scrollIntoView({ behavior: "auto" });
                }, 0);
            }
            return result;
        },
        [validateAndProcessOffer, navigate, loadSavedQuoteData, isDTO, isGIWL]
    );

    const initialState = { success: false };
    const [, formAction, isPending] = useActionState(async (prevState: typeof initialState, invitationCode: string) => {
        const result = await handleOfferResult(invitationCode, false);
        return { ...prevState, success: result.success };
    }, initialState);

    // ========== Automatic Offer Lookup ==========
    const { isLoading: autoLookupLoading } = useAutomaticOfferLookup({
        onOfferFound: handleOfferResult,
    });

    // ========== Render ==========
    return (
        <>
            <title>Welcome | AAA Life</title>
            <InvitationCodeCard
                onSubmit={formAction}
                isLoading={autoLookupLoading || offerLoading || isPending}
                error={error}
            />
        </>
    );
};

export default InvitationCodePage;
